import { addWeeks, endOfWeek } from "date-fns";
import { getParent, Instance, types } from "mobx-state-tree";
import { ID } from "@closerplatform/closer-sdk/dist/protocol/protocol";
import { ICalendarStore } from "./CalendarStore";
import { FormField } from "../../containers/calendar/dropdownMeetingEdit";
import { CalendarGuest, ICalendarGuest } from "./CalendarGuest";

export enum Mode {
  SCHEDULE = "SCHEDULE",
  RESCHEDULE = "RESCHEDULE",
  VIEW = "VIEW",
  DELETE = "DELETE",
  EDIT = "EDIT",
  SEARCH = "SEARCH",
  SCHEDULE_EXISTING = "SCHEDULE_EXISTING",
  SCHEDULE_NEW = "SCHEDULE_NEW"
}

export const MeetingPopup = types
  .model("MeetingPopup", {
    mode: types.enumeration("modes", Object.values(Mode)),
    start: types.maybe(types.Date),
    lastValidStart: types.maybe(types.Date),
    duration: types.maybe(types.number),
    roomId: types.maybe(types.string),
    selected: types.maybe(types.string),
    clickedField: types.maybe(types.string),
    profile: types.maybe(types.reference(CalendarGuest))
  })
  .actions((self) => ({
    afterAttach() {
      const calendarStore = getParent(self) as ICalendarStore;
      // we show slots a month ahead - check if calendarStore loaded required slots (meetings)
      const toDate = addWeeks(endOfWeek(Date.now(), { weekStartsOn: 1 }), 4).getTime();
      calendarStore.expandSupportedRangeIfNeeded(toDate);
    }
  }));
export type IMeetingPopup = Instance<typeof MeetingPopup>;
export type ScheduleType = IMeetingPopup & Schedule;
export type ViewType = IMeetingPopup & View;
export type RescheduleType = IMeetingPopup & Reschedule;
export type DeleteType = IMeetingPopup & Delete;
export type EditType = IMeetingPopup & Edit;
export type SearchType = IMeetingPopup & Search;
export type ScheduleNewType = IMeetingPopup & ScheduleNew;
export type ScheduleExistingType = IMeetingPopup & ScheduleExisting;

// TODO: clean up these interfaces ...
export interface PopupState {
  mode: Mode;
}

export interface Search extends PopupState {}

export interface ScheduleNew extends PopupState {
  start: Date;
  lastValidStart: Date;
  duration: number;
}

export interface Schedule extends ScheduleNew {
  roomId: ID;
}

export interface View extends Schedule {
  selected: string;
}

export interface Reschedule extends View {}

export interface Delete extends View {}

export interface Edit extends Schedule {
  clickedField: FormField; // TODO: importing from tsx
  selected?: string;
}

export interface ScheduleExisting extends ScheduleNew {
  profile: ICalendarGuest;
}

export const isSchedule = (state: IMeetingPopup): state is ScheduleType =>
  state.mode === Mode.SCHEDULE;

export const isView = (state: IMeetingPopup): state is ViewType => state.mode === Mode.VIEW;

export const isReschedule = (state: IMeetingPopup): state is RescheduleType =>
  state.mode === Mode.RESCHEDULE;

export const isDelete = (state: IMeetingPopup): state is DeleteType => state.mode === Mode.DELETE;

export const isEdit = (state: IMeetingPopup): state is EditType => state.mode === Mode.EDIT;

export const isSearch = (state: IMeetingPopup): state is SearchType => state.mode === Mode.SEARCH;

export const isScheduleNew = (state: IMeetingPopup): state is ScheduleNewType =>
  state.mode === Mode.SCHEDULE_NEW;

export const isScheduleExisting = (state: IMeetingPopup): state is ScheduleExistingType =>
  state.mode === Mode.SCHEDULE_EXISTING;

type SchedulingStateType = ScheduleType | RescheduleType | EditType;

export const isScheduling = (state: IMeetingPopup): state is SchedulingStateType =>
  isSchedule(state) || isReschedule(state) || isEdit(state);

export const scheduleState = (roomId: ID, start: Date, duration: number): Schedule => ({
  roomId,
  start,
  lastValidStart: start,
  duration,
  mode: Mode.SCHEDULE
});

export const viewState = (roomId: ID, start: Date, duration: number, selected: string): View => ({
  roomId,
  start,
  lastValidStart: start,
  duration,
  selected,
  mode: Mode.VIEW
});

export const rescheduleState = (
  roomId: ID,
  start: Date,
  duration: number,
  selected: string
): Reschedule => ({
  roomId,
  start,
  lastValidStart: start,
  duration,
  selected,
  mode: Mode.RESCHEDULE
});

export const deleteState = (
  roomId: ID,
  start: Date,
  duration: number,
  selected: string
): Delete => ({ roomId, start, lastValidStart: start, duration, selected, mode: Mode.DELETE });

export const editState = (
  roomId: ID,
  start: Date,
  lastValidStart: Date,
  duration: number,
  clickedField: FormField,
  selected?: string
): Edit => ({ roomId, start, lastValidStart, duration, clickedField, selected, mode: Mode.EDIT });

export const searchState = (): Search => ({ mode: Mode.SEARCH });

export const scheduleExistingState = (
  start: Date,
  duration: number,
  profile: ICalendarGuest
): ScheduleExisting => ({
  start,
  lastValidStart: start,
  duration,
  profile,
  mode: Mode.SCHEDULE_EXISTING
});

export const scheduleNewState = (start: Date, duration: number): ScheduleNew => ({
  start,
  lastValidStart: start,
  duration,
  mode: Mode.SCHEDULE_NEW
});
